/**
 * Copy a picked photo's bytes into a fresh in-memory File.
 *
 * Why the copy exists at all: Android WebView hands over Files that are lazy
 * streams on a TRANSIENT content:// permission grant — once the file input
 * resets, the grant can lapse and both the blob: preview and the upload read
 * fail (Pixel 4a, 2026-08-20). Copying while the grant is alive fixes that;
 * on iOS/web it's a harmless in-memory copy.
 *
 * Why the timeout exists: on iOS, a camera capture's arrayBuffer() can HANG
 * (never settle) after the WKWebView was suspended behind the camera UI
 * (iOS photo-capture bug, 2026-09-18). A hang here used to eat the whole
 * batch. Racing a timeout degrades to the ORIGINAL File for that file only —
 * exactly the fallback the error path has always used. A late resolution of
 * the losing promise is ignored and has no side effects.
 *
 * Never throws; never returns null for a real File.
 */
export const MATERIALIZE_TIMEOUT_MS = 8000;

export async function materializePickedFile(file, timeoutMs = MATERIALIZE_TIMEOUT_MS) {
  let timer = null;
  try {
    const copied = await Promise.race([
      (async () => new File([await file.arrayBuffer()], file.name, {
        type: file.type,
        lastModified: file.lastModified
      }))(),
      new Promise((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
      })
    ]);
    if (copied === null) {
      console.warn(`Materializing "${file.name}" timed out after ${timeoutMs}ms, using original`);
      return file;
    }
    return copied;
  } catch (err) {
    // Fall back to the original File rather than silently dropping the photo.
    console.warn('Could not materialize picked photo, using original:', err?.message || err);
    return file;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
